import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, ProgressBar, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faChevronDown, faChevronRight, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../providers/authprovider";
import ConfirmDeleteElementDialog from "../../components/forms/ConfirmDeleteElementDialog";
import OfferPriceTextArea from "./OfferPriceTextArea";
import OfferPriceGroupModal from "./OfferPriceGroupModal";
import OfferPriceExportModal from "./OfferPriceExportModal";

const inputStyle = {
  border: 0,
  padding: 0,
  boxShadow: "none",
  background: "transparent",
  textAlign: "center",
};

const cellStyle = {
  padding: 0,
};

const centeredCellStyle = {
  ...cellStyle,
  textAlign: "center",
  verticalAlign: "middle",
};

const draggingRowStyle = {
  opacity: 0.45,
};

const frozenTableStyle = {
  opacity: 0.55,
  pointerEvents: "none",
};

const unsavedFieldStyle = {
  color: "#dc3545",
};

const groupNameInputStyle = {
  border: 0,
  boxShadow: "none",
  background: "transparent",
  padding: 0,
  minWidth: 0,
};

function getPriceValue(price, multiplier) {
  if (price.quantity === null || price.quantity === "") return "";
  if (price.unitPrice === null || price.unitPrice === "") return "";

  return `${(
    Number(price.quantity) *
    Number(price.unitPrice) *
    Number(multiplier)
  ).toFixed(2)} zł`;
}

function getPriceNumber(price, multiplier) {
  if (price.quantity === null || price.quantity === "") return 0;
  if (price.unitPrice === null || price.unitPrice === "") return 0;

  return Number(price.quantity) * Number(price.unitPrice) * Number(multiplier);
}

function getGroupTotalValue(groupPrices, priceMultipliers) {
  return `${groupPrices
    .reduce((total, price) => total + getPriceNumber(price, priceMultipliers[price.id]), 0)
    .toFixed(2)} z\u0142`;
}

function preparePriceForSave(price) {
  return {
    name: price.name,
    description: price.description === "" ? null : price.description,
    quantity:
      price.quantity === null || price.quantity === "" ? null : Number(price.quantity),
    unitPrice:
      price.unitPrice === null || price.unitPrice === ""
        ? null
        : Number(price.unitPrice),
    group: price.group,
  };
}

function getRequestErrorMessage(result) {
  if (result.error) return result.error;

  return `Nie udało się zapisać danych. Status odpowiedzi: ${result.status}`;
}

function getLoadErrorMessage(result) {
  if (result.error) return result.error;

  return `Nie udało się pobrać danych. Status odpowiedzi: ${result.status}`;
}

function reorderPrices(currentPrices, draggedPriceId, targetPriceId, groupId) {
  const groupPrices = currentPrices.filter((price) => price.group === groupId);
  const draggedIndex = groupPrices.findIndex((price) => price.id === draggedPriceId);
  const targetIndex = groupPrices.findIndex((price) => price.id === targetPriceId);

  if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
    return currentPrices;
  }

  const reorderedGroupPrices = [...groupPrices];
  const [draggedPrice] = reorderedGroupPrices.splice(draggedIndex, 1);
  reorderedGroupPrices.splice(targetIndex, 0, draggedPrice);

  const groupQueue = [...reorderedGroupPrices];

  return currentPrices.map((price) =>
    price.group === groupId ? groupQueue.shift() : price
  );
}

function updateGroupPositions(currentPrices, groupId, updatedPrice) {
  const groupPrices = currentPrices.filter((price) => price.group === groupId);
  const positions = new Map(
    groupPrices.map((price, index) => [price.id, index + 1])
  );

  return currentPrices.map((price) => {
    if (price.group !== groupId) return price;

    const priceData =
      price.id === updatedPrice.id ? { ...price, ...updatedPrice } : price;

    return {
      ...priceData,
      position: positions.get(price.id),
    };
  });
}

export default function OfferPrices({ privilege }) {
  const { offerPrices } = useAuth();
  const [groups, setGroups] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState(null);
  const [draggingPriceId, setDraggingPriceId] = useState(null);
  const [frozenGroupId, setFrozenGroupId] = useState(null);
  const [creatingEmptyGroupId, setCreatingEmptyGroupId] = useState(null);
  const [tableErrors, setTableErrors] = useState({});
  const [pendingSaves, setPendingSaves] = useState({});
  const [selectedPriceIds, setSelectedPriceIds] = useState({});
  const [priceMultipliers, setPriceMultipliers] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportRows, setExportRows] = useState([]);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState({});
  const draggingPriceRef = useRef(null);
  const pricesBeforeDragRef = useRef(null);
  const currentGroupsRef = useRef([]);
  const currentPricesRef = useRef([]);
  const droppedInTableRef = useRef(false);
  const pendingSavesRef = useRef({});
  const saveDebounceTimersRef = useRef({});
  const saveRetryTimersRef = useRef({});
  const groupSaveDebounceTimersRef = useRef({});
  const saveVersionsRef = useRef({});

  const pricesByGroup = useMemo(() => {
    return groups.map((group) => ({
      group,
      prices: prices.filter((price) => price.group === group.id),
    }));
  }, [groups, prices]);

  useEffect(() => {
    currentPricesRef.current = prices;
  }, [prices]);

  useEffect(() => {
    currentGroupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    const retryTimers = saveRetryTimersRef.current;
    const debounceTimers = saveDebounceTimersRef.current;
    const groupDebounceTimers = groupSaveDebounceTimersRef.current;

    return () => {
      Object.values(debounceTimers).forEach((timerId) =>
        clearTimeout(timerId)
      );
      Object.values(retryTimers).forEach((timerId) =>
        clearTimeout(timerId)
      );
      Object.values(groupDebounceTimers).forEach((timerId) =>
        clearTimeout(timerId)
      );
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoadError("");
      setLoading(true);

      const groupsResult = await offerPrices.listGroups();
      if (!groupsResult.success) {
        setLoadError(getLoadErrorMessage(groupsResult));
        setLoading(false);
        return;
      }

      const pricesResult = await offerPrices.listPrices();
      if (!pricesResult.success) {
        setLoadError(getLoadErrorMessage(pricesResult));
        setLoading(false);
        return;
      }

      setGroups(groupsResult.content);
      setPrices(pricesResult.content);
      setSelectedPriceIds(
        pricesResult.content.reduce(
          (selectedIds, price) => ({
            ...selectedIds,
            [price.id]: false,
          }),
          {}
        )
      );
      setPriceMultipliers(
        pricesResult.content.reduce(
          (multipliers, price) => ({
            ...multipliers,
            [price.id]: 1.0,
          }),
          {}
        )
      );
      setLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePendingSaves = (updater) => {
    setPendingSaves((currentSaves) => {
      const nextSaves = updater(currentSaves);
      pendingSavesRef.current = nextSaves;
      return nextSaves;
    });
  };

  const updatePriceInState = (id, field, value) => {
    setPrices((currentPrices) => {
      const nextPrices = currentPrices.map((price) =>
        price.id === id ? { ...price, [field]: value } : price
      );
      currentPricesRef.current = nextPrices;
      return nextPrices;
    });
  };

  const updateGroupNameInState = (id, name) => {
    setGroups((currentGroups) => {
      const nextGroups = currentGroups.map((group) =>
        group.id === id ? { ...group, name } : group
      );
      currentGroupsRef.current = nextGroups;
      return nextGroups;
    });
  };

  const replacePriceInState = (updatedPrice) => {
    setPrices((currentPrices) => {
      const nextPrices = currentPrices.map((price) =>
        price.id === updatedPrice.id ? updatedPrice : price
      );
      currentPricesRef.current = nextPrices;
      return nextPrices;
    });
  };

  const replaceGroupInState = (updatedGroup) => {
    setGroups((currentGroups) => {
      const nextGroups = currentGroups.map((group) =>
        group.id === updatedGroup.id ? updatedGroup : group
      );
      currentGroupsRef.current = nextGroups;
      return nextGroups;
    });
  };

  const clearTableError = (groupId) => {
    setTableErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[groupId];
      return nextErrors;
    });
  };

  const showTableError = (groupId, error) => {
    setTableErrors((currentErrors) => ({
      ...currentErrors,
      [groupId]: error,
    }));
  };

  const clearPendingSave = (priceId) => {
    if (saveDebounceTimersRef.current[priceId]) {
      clearTimeout(saveDebounceTimersRef.current[priceId]);
      delete saveDebounceTimersRef.current[priceId];
    }

    if (saveRetryTimersRef.current[priceId]) {
      clearTimeout(saveRetryTimersRef.current[priceId]);
      delete saveRetryTimersRef.current[priceId];
    }
    delete saveVersionsRef.current[priceId];

    updatePendingSaves((currentSaves) => {
      const nextSaves = { ...currentSaves };
      delete nextSaves[priceId];
      return nextSaves;
    });
  };

  const markFieldAsUnsaved = (price, field) => {
    saveVersionsRef.current[price.id] =
      (saveVersionsRef.current[price.id] || 0) + 1;

    updatePendingSaves((currentSaves) => ({
      ...currentSaves,
      [price.id]: {
        groupId: price.group,
        fields: {
          ...(currentSaves[price.id]?.fields || {}),
          [field]: true,
        },
        status:
          currentSaves[price.id]?.status === "saving" ? "saving" : "pending",
        retryDelay: 5,
        retryIn: undefined,
      },
    }));
  };

  const schedulePendingSave = (priceId) => {
    if (saveDebounceTimersRef.current[priceId]) {
      clearTimeout(saveDebounceTimersRef.current[priceId]);
      delete saveDebounceTimersRef.current[priceId];
    }

    if (saveRetryTimersRef.current[priceId]) {
      clearTimeout(saveRetryTimersRef.current[priceId]);
      delete saveRetryTimersRef.current[priceId];
    }

    saveDebounceTimersRef.current[priceId] = setTimeout(() => {
      delete saveDebounceTimersRef.current[priceId];
      savePendingPrice(priceId);
    }, 500);
  };

  const savePendingPrice = async (priceId) => {
    const pendingSave = pendingSavesRef.current[priceId];
    const price = currentPricesRef.current.find(
      (currentPrice) => currentPrice.id === priceId
    );
    const saveVersion = saveVersionsRef.current[priceId] || 0;

    if (!pendingSave || !price) return;

    if (saveRetryTimersRef.current[priceId]) {
      clearTimeout(saveRetryTimersRef.current[priceId]);
      delete saveRetryTimersRef.current[priceId];
    }

    updatePendingSaves((currentSaves) => ({
      ...currentSaves,
      [priceId]: {
        ...currentSaves[priceId],
        status: "saving",
        retryIn: undefined,
      },
    }));
    clearTableError(pendingSave.groupId);

    const result = await offerPrices.updatePrice(
      priceId,
      preparePriceForSave(price)
    );

    if (!pendingSavesRef.current[priceId]) return;

    if (result.success) {
      if (saveVersionsRef.current[priceId] !== saveVersion) {
        updatePendingSaves((currentSaves) => ({
          ...currentSaves,
          [priceId]: {
            ...currentSaves[priceId],
            status: "pending",
            retryDelay: 5,
            retryIn: undefined,
          },
        }));
        schedulePendingSave(priceId);
        return;
      }

      replacePriceInState(result.content);
      clearPendingSave(priceId);
      clearTableError(pendingSave.groupId);
      return;
    }

    const retryIn = pendingSavesRef.current[priceId]?.retryDelay || 5;
    const errorMessage = getRequestErrorMessage(result);

    showTableError(
      pendingSave.groupId,
      `${errorMessage} Ponowna próba za ${retryIn} s.`
    );

    updatePendingSaves((currentSaves) => ({
      ...currentSaves,
      [priceId]: {
        ...currentSaves[priceId],
        status: "error",
        retryDelay: retryIn * 2,
        retryIn,
      },
    }));

    saveRetryTimersRef.current[priceId] = setTimeout(() => {
      savePendingPrice(priceId);
    }, retryIn * 1000);
  };

  const handleFieldChange = (price, field, value) => {
    updatePriceInState(price.id, field, value);
    markFieldAsUnsaved(price, field);
    schedulePendingSave(price.id);
  };

  const handleResetGroupQuantities = (groupPrices) => {
    groupPrices.forEach((price) => {
      handleFieldChange(price, "quantity", 0);
    });
  };

  const scheduleGroupSave = (groupId) => {
    if (groupSaveDebounceTimersRef.current[groupId]) {
      clearTimeout(groupSaveDebounceTimersRef.current[groupId]);
      delete groupSaveDebounceTimersRef.current[groupId];
    }

    groupSaveDebounceTimersRef.current[groupId] = setTimeout(() => {
      delete groupSaveDebounceTimersRef.current[groupId];
      saveGroup(groupId);
    }, 500);
  };

  const saveGroup = async (groupId) => {
    const group = currentGroupsRef.current.find(
      (currentGroup) => currentGroup.id === groupId
    );

    if (!group) return;

    clearTableError(groupId);

    const result = await offerPrices.updateGroup(groupId, group.name);
    if (!result.success) {
      showTableError(groupId, getRequestErrorMessage(result));
      return;
    }

    replaceGroupInState(result.content);
    clearTableError(groupId);
  };

  const handleGroupNameChange = (group, value) => {
    updateGroupNameInState(group.id, value);
    scheduleGroupSave(group.id);
  };

  const handleCreateEmpty = async (groupId) => {
    clearTableError(groupId);
    setCreatingEmptyGroupId(groupId);

    const result = await offerPrices.createEmptyPrice(groupId);
    if (!result.success) {
      setCreatingEmptyGroupId(null);
      showTableError(groupId, getRequestErrorMessage(result));
      return;
    }

    setPrices((currentPrices) => [...currentPrices, result.content]);
    setSelectedPriceIds((currentSelectedPriceIds) => ({
      ...currentSelectedPriceIds,
      [result.content.id]: true,
    }));
    setPriceMultipliers((currentMultipliers) => ({
      ...currentMultipliers,
      [result.content.id]: 1.0,
    }));
    setCreatingEmptyGroupId(null);
  };

  const handleDelete = async (price) => {
    clearTableError(price.group);

    const result = await offerPrices.deletePrice(price.id);
    if (!result.success) {
      showTableError(price.group, getRequestErrorMessage(result));
      return;
    }

    setPrices((currentPrices) =>
      currentPrices.filter((currentPrice) => currentPrice.id !== price.id)
    );
    setPriceMultipliers((currentMultipliers) => {
      const nextMultipliers = { ...currentMultipliers };
      delete nextMultipliers[price.id];
      return nextMultipliers;
    });
    clearPendingSave(price.id);
  };

  const handleConfirmDelete = () => {
    handleDelete(priceToDelete);
  };

  const isFieldUnsaved = (priceId, field) => {
    return Boolean(pendingSaves[priceId]?.fields?.[field]);
  };

  const togglePriceSelection = (priceId) => {
    setSelectedPriceIds((currentSelectedPriceIds) => ({
      ...currentSelectedPriceIds,
      [priceId]: !currentSelectedPriceIds[priceId],
    }));
  };

  const areAllGroupPricesSelected = (groupPrices) => {
    return groupPrices.length > 0 && groupPrices.every((price) => selectedPriceIds[price.id]);
  };

  const toggleGroupSelection = (groupPrices) => {
    const allSelected = areAllGroupPricesSelected(groupPrices);

    setSelectedPriceIds((currentSelectedPriceIds) => {
      const nextSelectedPriceIds = { ...currentSelectedPriceIds };
      groupPrices.forEach((price) => {
        nextSelectedPriceIds[price.id] = !allSelected;
      });
      return nextSelectedPriceIds;
    });
  };

  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroupIds((currentCollapsedGroupIds) => ({
      ...currentCollapsedGroupIds,
      [groupId]: !currentCollapsedGroupIds[groupId],
    }));
  };

  const getGroupSaveStatus = (groupId) => {
    const groupSaves = Object.values(pendingSaves).filter(
      (pendingSave) => pendingSave.groupId === groupId
    );

    if (groupSaves.some((pendingSave) => pendingSave.status === "saving")) {
      return "Zapisywanie...";
    }

    const errorSave = groupSaves.find(
      (pendingSave) => pendingSave.status === "error"
    );
    if (errorSave) {
      return `Nie zapisano. Ponowna próba za ${errorSave.retryIn} s.`;
    }

    if (groupSaves.length > 0) {
      return "Niezapisane zmiany";
    }

    return "";
  };

  const handleMultiplierChange = (priceId, value) => {
    setPriceMultipliers((currentMultipliers) => ({
      ...currentMultipliers,
      [priceId]: value,
    }));
  };

  const handleOpenExportModal = () => {
    setExportRows(
      prices
        .filter((price) => selectedPriceIds[price.id])
        .map((price) => ({
          ...price,
          unitPrice: Number(price.unitPrice) * Number(priceMultipliers[price.id]),
        }))
    );
    setShowExportModal(true);
  };

  const handleRowDragStart = (event, price) => {
    if (frozenGroupId !== null) return;

    draggingPriceRef.current = price;
    pricesBeforeDragRef.current = prices;
    droppedInTableRef.current = false;
    setDraggingPriceId(price.id);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", price.id);
  };

  const handleRowDragOver = (event, targetPrice) => {
    event.preventDefault();

    const draggedPrice = draggingPriceRef.current;
    if (!draggedPrice || draggedPrice.group !== targetPrice.group) return;

    event.dataTransfer.dropEffect = "move";
    setPrices((currentPrices) => {
      const reorderedPrices = reorderPrices(
        currentPrices,
        draggedPrice.id,
        targetPrice.id,
        targetPrice.group
      );
      currentPricesRef.current = reorderedPrices;
      return reorderedPrices;
    });
  };

  const handleRowDrop = (event) => {
    event.preventDefault();
    droppedInTableRef.current = true;
  };

  const handleRowDragEnd = async () => {
    const draggedPrice = draggingPriceRef.current;

    draggingPriceRef.current = null;
    setDraggingPriceId(null);

    if (!draggedPrice) return;

    if (!droppedInTableRef.current) {
      setPrices(pricesBeforeDragRef.current);
      pricesBeforeDragRef.current = null;
      return;
    }

    const currentGroupPrices = currentPricesRef.current.filter(
      (price) => price.group === draggedPrice.group
    );
    const nextPosition =
      currentGroupPrices.findIndex((price) => price.id === draggedPrice.id) + 1;
    const previousPosition =
      pricesBeforeDragRef.current
        .filter((price) => price.group === draggedPrice.group)
        .findIndex((price) => price.id === draggedPrice.id) + 1;

    if (nextPosition === previousPosition) {
      pricesBeforeDragRef.current = null;
      return;
    }

    setFrozenGroupId(draggedPrice.group);
    clearTableError(draggedPrice.group);

    const result = await offerPrices.updatePricePosition(
      draggedPrice.id,
      nextPosition
    );

    if (!result.success) {
      setPrices(pricesBeforeDragRef.current);
      setFrozenGroupId(null);
      pricesBeforeDragRef.current = null;
      showTableError(draggedPrice.group, getRequestErrorMessage(result));
      return;
    }

    setPrices((currentPrices) =>
      updateGroupPositions(currentPrices, draggedPrice.group, result.content)
    );
    setFrozenGroupId(null);
    pricesBeforeDragRef.current = null;
  };

  return (
    <div className="container p-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Cennik</h4>
        {privilege.edit && (
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handleOpenExportModal}>
              Eksportuj do oferty
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => setShowCreateGroupModal(true)}
            >
              Utwórz grupę
            </Button>
          </div>
        )}
      </div>
      {loadError && <Alert variant="danger">{loadError}</Alert>}
      {loading && !loadError && <Alert variant="info">Dane są pobierane...</Alert>}
      {!loading && !loadError && groups.length === 0 && (
        <Alert variant="info">
          Cennik nie ma żadnych grup. Użytkownik musi utworzyć grupę, żeby dodać
          pozycje.
        </Alert>
      )}
      {!loading && !loadError && pricesByGroup.map(({ group, prices: groupPrices }) => {
        const isGroupCollapsed = collapsedGroupIds[group.id] === true;

        return (
          <div className="mb-4" key={group.id}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                title={isGroupCollapsed ? "Rozwiń grupę" : "Zwiń grupę"}
                aria-expanded={!isGroupCollapsed}
                onClick={() => toggleGroupCollapse(group.id)}
              >
                <FontAwesomeIcon icon={isGroupCollapsed ? faChevronRight : faChevronDown} />
              </Button>
              <Form.Control
                className="fs-5 fw-semibold flex-grow-1"
                value={group.name}
                readOnly={!privilege.edit}
                aria-label="Nazwa grupy"
                style={groupNameInputStyle}
                onChange={(event) => handleGroupNameChange(group, event.target.value)}
              />
              {isGroupCollapsed && groupPrices.length > 0 && (
                <div className="fw-semibold text-nowrap">
                  Suma: {getGroupTotalValue(groupPrices, priceMultipliers)}
                </div>
              )}
            </div>
            {tableErrors[group.id] && (
              <Alert variant="danger">{tableErrors[group.id]}</Alert>
            )}
            {!isGroupCollapsed && (
              <Table
                bordered
                hover={draggingPriceId === null && frozenGroupId === null}
                responsive
                className="fs-6"
                style={frozenGroupId === group.id ? frozenTableStyle : undefined}
              >
            <thead>
              <tr>
                <th width="60" style={centeredCellStyle}>
                  <Form.Check
                    checked={areAllGroupPricesSelected(groupPrices)}
                    onChange={() => toggleGroupSelection(groupPrices)}
                  />
                </th>
                <th>Nazwa</th>
                <th>Opis</th>
                <th width="120">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <span>Ilość</span>
                    {privilege.edit && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-reset"
                        title="Resetuj ilości"
                        onClick={() => handleResetGroupQuantities(groupPrices)}
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} />
                      </Button>
                    )}
                  </div>
                </th>
                <th width="160">Cena jednostkowa</th>
                <th width="120">Mnożnik</th>
                <th width="160">Wartość</th>
                {privilege.edit && <th width="80">Akcje</th>}
              </tr>
            </thead>
            <tbody>
              {groupPrices.map((price) => (
                <tr
                  key={price.id}
                  draggable={privilege.edit && frozenGroupId === null}
                  onDragStart={(event) => handleRowDragStart(event, price)}
                  onDragOver={(event) => handleRowDragOver(event, price)}
                  onDrop={handleRowDrop}
                  onDragEnd={handleRowDragEnd}
                  style={price.id === draggingPriceId ? draggingRowStyle : undefined}
                >
                  <td style={centeredCellStyle}>
                    <Form.Check
                      checked={Boolean(selectedPriceIds[price.id])}
                      onChange={() => togglePriceSelection(price.id)}
                    />
                  </td>
                  <td style={cellStyle}>
                    <OfferPriceTextArea
                      value={price.name}
                      readOnly={!privilege.edit}
                      onChange={(event) =>
                        handleFieldChange(price, "name", event.target.value)
                      }
                      style={isFieldUnsaved(price.id, "name") ? unsavedFieldStyle : undefined}
                    />
                  </td>
                  <td style={cellStyle}>
                    <OfferPriceTextArea
                      value={price.description ?? ""}
                      readOnly={!privilege.edit}
                      onChange={(event) =>
                        handleFieldChange(
                          price,
                          "description",
                          event.target.value
                        )
                      }
                      style={
                        isFieldUnsaved(price.id, "description")
                          ? unsavedFieldStyle
                          : undefined
                      }
                    />
                  </td>
                  <td style={centeredCellStyle}>
                    <Form.Control
                      className="fs-6"
                      type="number"
                      value={price.quantity ?? ""}
                      readOnly={!privilege.edit}
                      style={{
                        ...inputStyle,
                        ...(isFieldUnsaved(price.id, "quantity")
                          ? unsavedFieldStyle
                          : {}),
                      }}
                      onChange={(event) =>
                        handleFieldChange(
                          price,
                          "quantity",
                          event.target.value
                        )
                      }
                    />
                  </td>
                  <td style={centeredCellStyle}>
                    <Form.Control
                      className="fs-6"
                      type="number"
                      value={price.unitPrice ?? ""}
                      readOnly={!privilege.edit}
                      style={{
                        ...inputStyle,
                        ...(isFieldUnsaved(price.id, "unitPrice")
                          ? unsavedFieldStyle
                          : {}),
                      }}
                      onChange={(event) =>
                        handleFieldChange(
                          price,
                          "unitPrice",
                          event.target.value
                        )
                      }
                    />
                  </td>
                  <td style={centeredCellStyle}>
                    <Form.Control
                      className="fs-6"
                      type="number"
                      step="0.1"
                      value={priceMultipliers[price.id]}
                      style={inputStyle}
                      onChange={(event) =>
                        handleMultiplierChange(price.id, event.target.value)
                      }
                    />
                  </td>
                  <td style={centeredCellStyle}>
                    {getPriceValue(price, priceMultipliers[price.id])}
                  </td>
                  {privilege.edit && (
                    <td style={centeredCellStyle}>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        title="Usun"
                        onClick={() => setPriceToDelete(price)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {groupPrices.length > 0 && (
                <tr>
                  <td colSpan={6} className="text-end fw-semibold">
                    Suma
                  </td>
                  <td style={centeredCellStyle} className="fw-semibold">
                    {getGroupTotalValue(groupPrices, priceMultipliers)}
                  </td>
                  {privilege.edit && <td></td>}
                </tr>
              )}
              {privilege.edit && (
                <tr>
                  <td colSpan={8}>
                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        className="fs-6"
                        variant="outline-primary"
                        size="sm"
                        disabled={creatingEmptyGroupId === group.id}
                        onClick={() => handleCreateEmpty(group.id)}
                      >
                        {creatingEmptyGroupId === group.id
                          ? "Zapisywanie..."
                          : "+ dodaj pozycję"}
                      </Button>
                      {getGroupSaveStatus(group.id) && (
                        <div className="d-flex align-items-center gap-2">
                          {getGroupSaveStatus(group.id) === "Zapisywanie..." && (
                            <>
                              <Spinner animation="border" size="sm" />
                              <ProgressBar
                                animated
                                now={100}
                                style={{ width: "120px", height: "6px" }}
                              />
                            </>
                          )}
                          <small className="text-muted">
                            {getGroupSaveStatus(group.id)}
                          </small>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
              </Table>
            )}
          </div>
        );
      })}
      <OfferPriceGroupModal
        show={showCreateGroupModal}
        onHide={() => setShowCreateGroupModal(false)}
        onCreated={(group) =>
          setGroups((currentGroups) => [...currentGroups, group])
        }
      />
      <OfferPriceExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        exportRows={exportRows}
      />
      <ConfirmDeleteElementDialog
        show={priceToDelete !== null}
        onClose={() => setPriceToDelete(null)}
        onConfirm={handleConfirmDelete}
        message="Czy na pewno usunąć pozycję z cennika?"
      />
    </div>
  );
}
